/** @jsx h */
import { h, Fragment } from "preact";
import { useContext, useEffect, useMemo, useState } from "preact/hooks";
import { renderPreact } from "../lib/render-preact";
import PageContext from "./PageContext";
import { translate, t } from "../I18n";
import Alert from "./Alert";
import PreactTooltip from "./PreactTooltip";
import { formatRestaurantAddress } from "../utils/formatAddress";
import { formatOpeningHours } from "../utils/formatOpeningHours";
import { formatAddressAsSingleLine, formatFulfillmentMethodType, GeocodingService, LegacyStreetAddressStorageManagerAdapter, LocalizationProvider, Money, RestaumaticRPCService, StreetAddress, StreetAddressFormController, StreetAddressStorageManager, WebGeolocationService, } from "restaumatic-client/src";
import { observer } from "mobx-react-lite";
import StreetAddressForm from "./StreetAddressForm";
import LocateMeButton from "./LocateMeButton";
import { Heading } from "./Heading";
import useEventEffect from "../utils/useEventEffect";
import useScrollIfAboveEffect from "../utils/useScrollIfAboveEffect";
const restaurantSelectionId = "restaurant-selection";
// Focus on top of the restaurant selection module
const focusRestaurantSelection = () => {
    const restaurantSelectionEl = document.getElementById(restaurantSelectionId);
    if (restaurantSelectionEl) {
        restaurantSelectionEl.focus({ preventScroll: true });
    }
};
// Sort the list based on the 'isSelected' property (true value come first)
const sortRestaurants = (list) => {
    return list.sort((a, b) => {
        if (a.isSelected === b.isSelected) {
            return 0;
        }
        return a.isSelected ? -1 : 1;
    });
};
// Mark current restaurant as selected.
const updateRestaurantsWithLatest = (restaurants) => {
    if (typeof window === "undefined")
        return restaurants;
    const currentRestaurantSlug = window.localStorage.getItem("currentRestaurant");
    if (!currentRestaurantSlug)
        return restaurants;
    restaurants = restaurants.map((restaurant) => ({
        ...restaurant,
        isSelected: restaurant.slug === currentRestaurantSlug,
    }));
    return sortRestaurants(restaurants);
};
function toCardProps(localizationProvider, r) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const deliveryCost = (_b = (_a = r.deliveryInfo) === null || _a === void 0 ? void 0 : _a.deliveryCost) !== null && _b !== void 0 ? _b : null;
    const minOrderValue = (_d = (_c = r.deliveryInfo) === null || _c === void 0 ? void 0 : _c.minOrderValue) !== null && _d !== void 0 ? _d : null;
    const freeThreshold = (_f = (_e = r.deliveryInfo) === null || _e === void 0 ? void 0 : _e.freeDeliveryThreshold) !== null && _f !== void 0 ? _f : null;
    return {
        id: r.id,
        name: r.name,
        slug: (_g = r.slug) !== null && _g !== void 0 ? _g : "",
        address: formatRestaurantAddress(r.address),
        phones: r.contactPhoneNumber
            ? r.contactPhoneNumber.split(",").map((s) => s.trim())
            : [],
        email: (_h = r.contactEmail) !== null && _h !== void 0 ? _h : "",
        deliveryTypes: r.fulfillmentMethods
            .map(formatFulfillmentMethodType)
            .join(", "),
        menuLink: `/restauracja/${r.slug}`,
        isWithinArea: !!r.deliveryInfo,
        isSelected: false,
        openingHours: formatOpeningHours(r.restaurantHours),
        deliveryCost: deliveryCost !== null
            ? localizationProvider.formatPrice(Money.fromBackend(deliveryCost))
            : null,
        minOrderValue: minOrderValue !== null
            ? localizationProvider.formatPrice(Money.fromBackend(minOrderValue))
            : null,
        freeTreshold: freeThreshold !== null
            ? localizationProvider.formatPrice(Money.fromBackend(freeThreshold))
            : null,
    };
}
function RestaurantSelection(props) {
    const settings = useContext(PageContext);
    const localizationProvider = new LocalizationProvider({
        locale: settings.locale,
        fallbackLocale: settings.defaultLocale,
        currency: settings.currency,
    });
    const [backendRestaurants, setBackendRestaurants] = useState(props.initialRestaurants);
    const restaurants = updateRestaurantsWithLatest(backendRestaurants.map((r) => toCardProps(localizationProvider, r)));
    const { titleExtraClass = "", cardTitleExtraClass = "", cardExtraClass = "", listColumnNumber = 3, listHeadingLevel = "h3", cardHeadingLevel = "h4", } = props;
    const [rootKey, setRootKey] = useState(0);
    const [isFormVisible, setIsFormVisible] = useState(true);
    const [scrolling, setScrolling] = useState(false);
    const [loading, setLoading] = useState(false);
    const [geocodingError, setGeocodingError] = useState(false);
    const [streetAddress, setStreetAddress] = useState(() => StreetAddress.empty(settings.country));
    const [addressEntered, setAddressEntered] = useState(false);
    const [currentAddress, setCurrentAddress] = useState(null);
    const deliveringRestaurants = restaurants.filter((item) => item.isWithinArea);
    const otherRestaurants = restaurants.filter((item) => !item.isWithinArea);
    const onEdit = () => {
        setIsFormVisible(true);
        setScrolling(true);
        focusRestaurantSelection();
    };
    const rpcService = new RestaumaticRPCService({ url: "" });
    const geocodingService = new GeocodingService({
        rpcService: rpcService,
        siteId: settings.siteId,
    });
    const showDeliveringRestaurants = currentAddress !== null && deliveringRestaurants.length > 0;
    const noDeliveryError = addressEntered && !loading && deliveringRestaurants.length === 0;
    // Add event listeners for changing selected restaurants with `select_restaurant.liquid`
    // Re-render component to update restaurants with new `isSelected` value
    useEventEffect("change-restaurant.restaumatic", () => {
        setRootKey((prevRootKey) => prevRootKey + 1);
    });
    // Scrolling to the top of restaurant selection module
    useScrollIfAboveEffect(scrolling, restaurantSelectionId, () => {
        setScrolling(false);
    });
    const onSubmit = (streetAddress) => {
        setLoading(true);
        setGeocodingError(false);
        setAddressEntered(true);
        new StreetAddressStorageManager().store(streetAddress.storageData);
        new LegacyStreetAddressStorageManagerAdapter(streetAddress).store();
        geocodingService
            .geocode(settings.country, streetAddress)
            .then((coordinates) => {
            if (!coordinates) {
                setGeocodingError(true);
                return;
            }
            rpcService
                .rpc("GetRestaurants", {
                siteId: settings.siteId,
                location: coordinates,
            })
                .then((restaurants) => {
                setCurrentAddress(streetAddress.clone());
                setBackendRestaurants(restaurants);
                const anyDelivers = restaurants.some((item) => item.deliveryInfo);
                if (anyDelivers) {
                    setIsFormVisible(false);
                    if (!addressEntered)
                        return;
                    setScrolling(true);
                }
                else {
                    setIsFormVisible(true);
                    if (!addressEntered)
                        return;
                    focusRestaurantSelection();
                }
            })
                .finally(() => {
                setLoading(false);
            });
        });
    };
    // Restore from storage. Falls back to legacy storage for migration
    useEffect(() => {
        let restoredStreetAddress = StreetAddress.fromStorageData({
            country: settings.country,
            storageData: new StreetAddressStorageManager().restore(),
        });
        if (restoredStreetAddress.isEmpty) {
            new LegacyStreetAddressStorageManagerAdapter(restoredStreetAddress).restore();
        }
        if (!restoredStreetAddress.isEmpty) {
            setStreetAddress(restoredStreetAddress);
            onSubmit(restoredStreetAddress);
        }
    }, []);
    return (h("div", { id: restaurantSelectionId, key: rootKey, className: "m-restaurants", tabIndex: -1, "aria-live": "polite" },
        isFormVisible && (h("article", { className: "m-restaurants__content" },
            h("div", { className: "m-restaurants__form-wrapper" },
                h(AddressForm, { titleExtraClass: titleExtraClass, listHeadingLevel: listHeadingLevel, geocodingError: geocodingError, noDeliveryError: noDeliveryError, streetAddress: streetAddress, onSubmit: () => onSubmit(streetAddress) })))),
        !showDeliveringRestaurants && (h(RestaurantList, { title: translate(t.restaurants.select.header_all), showAddressInHeader: false, list: restaurants, titleExtraClass: titleExtraClass, cardTitleExtraClass: cardTitleExtraClass, cardExtraClass: cardExtraClass, listColumnNumber: listColumnNumber, listHeadingLevel: listHeadingLevel, cardHeadingLevel: cardHeadingLevel, address: currentAddress })),
        showDeliveringRestaurants && (h(Fragment, null,
            h(RestaurantList, { title: translate(t.restaurants.select.header_deliver), list: deliveringRestaurants, titleExtraClass: titleExtraClass, cardTitleExtraClass: cardTitleExtraClass, cardExtraClass: cardExtraClass, listColumnNumber: listColumnNumber, listHeadingLevel: listHeadingLevel, cardHeadingLevel: cardHeadingLevel, doesRestaurantDeliver: true, onEdit: onEdit, address: currentAddress }),
            h(RestaurantList, { title: translate(t.restaurants.select.header_others), list: otherRestaurants, titleExtraClass: titleExtraClass, cardTitleExtraClass: cardTitleExtraClass, cardExtraClass: cardExtraClass, listColumnNumber: listColumnNumber, listHeadingLevel: listHeadingLevel, cardHeadingLevel: cardHeadingLevel, doesRestaurantDeliver: false, onEdit: onEdit, address: currentAddress })))));
}
const AddressForm = observer((props) => {
    const { titleExtraClass, listHeadingLevel, onSubmit, geocodingError, noDeliveryError, streetAddress, } = props;
    const settings = useContext(PageContext);
    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit();
    };
    // Note: controller is reinitialized whenever we get a new StreetAddress object,
    // which happens e.g. when loading from storage
    const controller = useMemo(() => {
        const geocodingService = new GeocodingService({
            rpcService: new RestaumaticRPCService({ url: "" }),
            siteId: settings.siteId,
        });
        const geolocationService = new WebGeolocationService();
        return new StreetAddressFormController({
            streetAddress,
            geocodingService,
            geolocationService,
        });
    }, [streetAddress]);
    const [geolocationError, setGeolocationError] = useState(null);
    return (h(Fragment, null,
        h(Heading, { level: listHeadingLevel, className: `m-restaurants__heading ${titleExtraClass}`, id: "restaurant-selection-form-heading" }, translate(t.restaurants.select.title_find)),
        h("form", { "aria-controls": "restaurant-selection-list", "aria-labelledby": "restaurant-selection-form-heading", onSubmit: handleSubmit },
            h(LocateMeButton, { onLocateMe: () => {
                    setGeolocationError(null);
                    controller
                        .locateMe()
                        .then((success) => {
                        if (success) {
                            onSubmit();
                        }
                        else {
                            setGeolocationError(translate(t.geolocator.address_missing));
                        }
                    })
                        .catch((e) => {
                        if (e instanceof GeolocationPositionError) {
                            setGeolocationError(e.code === GeolocationPositionError.PERMISSION_DENIED
                                ? translate(t.geolocator.no_permission)
                                : translate(t.geolocator.failed_to_locate));
                        }
                    });
                }, geolocationError: geolocationError !== null && geolocationError !== void 0 ? geolocationError : undefined }),
            h("div", { className: "m-delivery-address__container" },
                h(StreetAddressForm, { controller: controller, isPostCodeRequired: streetAddress.isPostCodeRequired })),
            h("button", { type: "submit", className: "btn btn-primary" }, translate(t.restaurants.select.find_restaurant))),
        geocodingError && (h(Alert, { type: "danger", message: translate(t.delivery.geocoding_error) })),
        streetAddress && noDeliveryError && (h(NoDeliveryAlert, { address: streetAddress }))));
});
function RestaurantList(props) {
    const { title, list, titleExtraClass, cardTitleExtraClass, cardExtraClass, listColumnNumber, listHeadingLevel, cardHeadingLevel, doesRestaurantDeliver, onEdit, address, } = props;
    if (list.length === 0) {
        return h(Fragment, null);
    }
    return (h("article", { className: "m-restaurants__content" },
        h(Heading, { level: listHeadingLevel, className: `m-restaurants__heading ${titleExtraClass}`, id: "restaurant-selection-list-heading" },
            h("span", null,
                title,
                " "),
            address &&
                doesRestaurantDeliver !== false &&
                props.showAddressInHeader !== false && (h(Fragment, null,
                h("span", null,
                    formatAddressAsSingleLine(address, address.isPostCodeRequired),
                    "\u00A0"),
                h("button", { type: "button", className: "btn btn-link u-p0 u-box-shadow-none", onClick: onEdit }, "(" + translate(t.restaurants.select.button_change) + ")")))),
        h("ul", { className: "list-unstyled row row--flex row--vertical-gutter", id: "restaurant-selection-list", "aria-labelledby": "restaurant-selection-list-heading" }, list.map((item) => (h(RestaurantCard, { restaurant: item, titleExtraClass: cardTitleExtraClass, cardExtraClass: cardExtraClass, listColumnNumber: listColumnNumber, cardHeadingLevel: cardHeadingLevel, doesRestaurantDeliver: doesRestaurantDeliver, onEdit: onEdit, address: address }))))));
}
function RestaurantCard(props) {
    const { restaurant, doesRestaurantDeliver, titleExtraClass, cardExtraClass, listColumnNumber, cardHeadingLevel, onEdit, address, } = props;
    const openingHours = restaurant.openingHours.split("\n");
    const columnGridWidth = 12 / listColumnNumber; // 12 is number of columns specified by Bootstrap Grid System
    const freeTresholdString = restaurant.freeTreshold
        ? ", " +
            translate(t.theme_defaults.delivery.info.free_treshold) +
            restaurant.freeTreshold
        : "";
    return (h("li", { className: `col-sm-6 col-md-${columnGridWidth}` },
        h("div", { class: `m-restaurants__card ${cardExtraClass} ${restaurant.isSelected ? "m-restaurants__card--selected" : ""}` },
            h(Heading, { level: cardHeadingLevel, className: `m-restaurants__card-title ${titleExtraClass}` }, restaurant.name),
            h("ul", { className: "u-list-unstyled u-flex-grow" },
                h("li", { className: "d-flex u-my3" }, restaurant.address),
                h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-time icon-base", "aria-hidden": "true" }),
                    h("div", null, openingHours.map((line) => (h(Fragment, null,
                        line,
                        h("br", null)))))),
                restaurant.phones.length > 0 && (h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-phone icon-base", "aria-hidden": "true" }),
                    h("div", null, restaurant.phones.map((item) => (h("a", { href: `tel:${item}`, className: "u-block u-link-unstyled" }, item)))))),
                restaurant.email && (h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-mail icon-base", "aria-hidden": "true" }),
                    h("a", { href: `mailto:${restaurant.email}`, className: "u-link-unstyled u-text-ellipsis" }, restaurant.email))),
                h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-shopping-cart icon-base", "aria-hidden": "true" }),
                    h("span", null, restaurant.deliveryTypes)),
                doesRestaurantDeliver === true && (h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-truck icon-base", "aria-hidden": "true" }),
                    h("span", null,
                        restaurant.deliveryCost,
                        " (min. ",
                        restaurant.minOrderValue,
                        ")",
                        h(PreactTooltip, { class: "btn btn-link u-p0 u-text-decoration-none u-box-shadow-none", title: `${translate(t.theme_defaults.delivery.info.cost)}${restaurant.deliveryCost}, ${translate(t.theme_defaults.delivery.info.min_order_value)}${restaurant.minOrderValue}${freeTresholdString}` },
                            h("span", { className: "icon-info-sign icon-flex-center", "aria-label": translate(t.theme_defaults.theme.show_more) })))))),
            address && doesRestaurantDeliver === false && (h(NoWithinAreaAlert, { onEdit: onEdit, address: address })),
            h("div", null,
                h("a", { href: restaurant.menuLink, className: "m-restaurants__card-action btn btn-primary" }, translate(t.restaurants.select.button_select))))));
}
function NoWithinAreaAlert(props) {
    const { onEdit, address } = props;
    return (h(Fragment, null,
        h(Alert, { type: "warning", extraClasses: "u-my2", message: translate(t.delivery.not_possible, {
                address: formatAddressAsSingleLine(address, address.isPostCodeRequired),
            }), link: {
                label: `(${translate(t.restaurants.select.button_change)})`,
                path: "#" + restaurantSelectionId,
                onClick: onEdit
                    ? (e) => {
                        e.preventDefault();
                        onEdit();
                    }
                    : undefined,
            } })));
}
function NoDeliveryAlert(props) {
    const { address } = props;
    const alertMessage = `${translate(t.restaurants.select.error_address)} ${formatAddressAsSingleLine(address, address.isPostCodeRequired)}`;
    return (h(Fragment, null,
        h(Alert, { type: "danger", extraClasses: "u-my3", message: alertMessage })));
}
export default renderPreact("RestaurantSelection", observer(RestaurantSelection));
